import React from 'react';
import { Button, Space, Select, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/**
 * Helper function to create filter action buttons (Filter/Reset)
 * 
 * @param {function} onFilter - Callback when filter button is clicked
 * @param {function} onReset - Callback when reset button is clicked
 * @param {string} buttonText - Text for the filter button (default: 'Filter')
 * @returns {JSX.Element} Filter action buttons
 */
export const createFilterButtons = (onFilter, onReset, buttonText = 'Filter') => (
    <Space>
        <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={onFilter}
            size="small"
            style={{ width: 90 }}
        >
            {buttonText}
        </Button>
        <Button
            onClick={onReset}
            size="small"
            style={{ width: 90 }}
        >
            Reset
        </Button>
    </Space>
);

/**
 * Helper function to create a searchable select-based filter dropdown
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.placeholder - Placeholder text for the select
 * @param {Array<string>} config.options - Array of option values to display
 * @param {Array} config.selectedKeys - Currently selected keys
 * @param {function} config.setSelectedKeys - Function to update selected keys
 * @param {function} config.onConfirm - Callback when filter is confirmed
 * @param {function} config.onReset - Callback when filter is reset
 * @param {boolean} config.useOptionFilterProp - Whether to use optionFilterProp="children" (default: false)
 * @returns {JSX.Element} Select-based filter dropdown
 */
export const createSelectFilter = ({
    placeholder,
    options,
    selectedKeys,
    setSelectedKeys,
    onConfirm,
    onReset,
    useOptionFilterProp = false
}) => (
    <div style={{ padding: 8 }}>
        <Select
            style={{ width: 200, marginBottom: 8, display: 'block' }}
            placeholder={placeholder}
            value={selectedKeys[0]}
            onChange={(value) => {
                setSelectedKeys(value ? [value] : []);
            }}
            allowClear
            showSearch
            dropdownStyle={{ zIndex: 9999 }}
            {...(useOptionFilterProp && { optionFilterProp: "children" })}
            filterOption={(input, option) => {
                const searchText = input.toLowerCase();
                const optionText = String(option?.children ?? option?.value ?? '').toLowerCase();
                return optionText.includes(searchText);
            }}
        >
            {options.map(option => (
                <Select.Option key={option} value={option}>
                    {option}
                </Select.Option>
            ))}
        </Select>
        {createFilterButtons(onConfirm, onReset)}
    </div>
);

/**
 * Helper function to create an input-based filter dropdown
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.placeholder - Placeholder text for the input
 * @param {Array} config.selectedKeys - Currently selected keys
 * @param {function} config.setSelectedKeys - Function to update selected keys
 * @param {function} config.onConfirm - Callback when filter is confirmed
 * @param {function} config.onReset - Callback when filter is reset
 * @param {string} config.buttonText - Text for the filter button (default: 'Search')
 * @returns {JSX.Element} Input-based filter dropdown
 */
export const createInputFilter = ({
    placeholder,
    selectedKeys,
    setSelectedKeys,
    onConfirm,
    onReset,
    buttonText = 'Search'
}) => (
    <div style={{ padding: 8 }}>
        <Input
            placeholder={placeholder}
            value={selectedKeys[0]}
            onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onPressEnter={onConfirm}
            style={{ marginBottom: 8, display: 'block' }}
        />
        {createFilterButtons(onConfirm, onReset, buttonText)}
    </div>
);

